import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { opsApi, ApiRequestError, type OpsRole, type OpsUser } from '@pinchcrew/core'

export const useAuthStore = defineStore('ops-auth', () => {
  const user = ref<OpsUser | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  const isAuthenticated = computed(() => user.value !== null)
  const role = computed<OpsRole | null>(() => user.value?.role ?? null)

  /**
   * Role capabilities, mirrored from the backend's gates. The UI hides what a
   * role cannot do, but the server is what enforces it — hiding a button is
   * not access control.
   */
  const can = computed(() => ({
    vet: role.value === 'reviewer' || role.value === 'admin',
    money: role.value === 'finance' || role.value === 'admin',
    disputes: role.value === 'support' || role.value === 'admin',
    orgs: role.value === 'admin',
    rates: role.value === 'admin' || role.value === 'finance',
    publishCourses: role.value === 'admin',
    editCourses: role.value === 'admin' || role.value === 'reviewer',
  }))

  async function login(email: string, password: string, totp?: string) {
    loading.value = true
    error.value = null
    try {
      const r = await opsApi.login(email, password, totp)
      user.value = r.user
      return true
    } catch (e) {
      error.value = e instanceof ApiRequestError ? e.message : 'Could not sign in.'
      return false
    } finally {
      loading.value = false
    }
  }

  /** Restore a session from a stored token on boot; see the worker store. */
  async function restore() {
    if (!opsApi.hasStoredSession()) return false
    try {
      user.value = await opsApi.getMe()
      return true
    } catch {
      await logout()
      return false
    }
  }

  async function logout() {
    await opsApi.logout()
    user.value = null
  }

  return { user, loading, error, isAuthenticated, role, can, login, restore, logout }
})
